import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { AreaSensivel, CampoControlado } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { mensagemDeErro, post } from '../../nucleo/api';
import { dinheiro, simboloMoeda } from '../../nucleo/formato';
import { exemploDinheiroEm } from '../../nucleo/mascaras';
import { hojeLocal, invalidarContas, type Documento } from './tipos';

/** Documentos que podem ir juntos numa renegociação: fatura ou conta a pagar, em aberto, do mesmo parceiro e tipo. */
export function renegociaveisJuntos(docs: Pick<Documento, 'tipo' | 'estado' | 'parceiro_id' | 'moeda'>[]): boolean {
  if (!docs.length) return false;
  const [a] = docs;
  return docs.every((d) => (d.tipo === 'fatura' || d.tipo === 'conta_pagar') && (d.estado === 'em_aberto' || d.estado === 'parcial') && d.tipo === a!.tipo && d.parceiro_id === a!.parceiro_id && d.moeda === a!.moeda);
}

/** Total das parcelas novas e o valor aproximado de cada uma (o servidor reparte pelo maior resto). */
export function previaRenegociacao(saldo: number, juros: number, multa: number, desconto: number, parcelas: number) {
  const total = saldo + juros + multa - desconto;
  return { total, parcela: parcelas > 0 ? Math.ceil(total / parcelas) : 0 };
}

/**
 * Fase 2 (W14, F6): renegociação dos documentos escolhidos (`POST /contas/documentos/renegociar`). O saldo sai dos
 * documentos (ficam renegociados) e vira N parcelas novas, com juros, multa e desconto negociados à vista na tela.
 */
export function Renegociar({ docs, aoFechar, aoFeito }: { docs: Documento[] | null; aoFechar: () => void; aoFeito: (parcelas: string[]) => void }) {
  if (!docs?.length) return null;
  return <Corpo docs={docs} aoFechar={aoFechar} aoFeito={aoFeito} />;
}

function Corpo({ docs, aoFechar, aoFeito }: { docs: Documento[]; aoFechar: () => void; aoFeito: (parcelas: string[]) => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [chave] = useState(() => crypto.randomUUID());
  const [parcelas, setParcelas] = useState<number | null>(3);
  const [primeiro, setPrimeiro] = useState(hojeLocal());
  const [intervalo, setIntervalo] = useState<number | null>(30);
  const [juros, setJuros] = useState<number | null>(null);
  const [multa, setMulta] = useState<number | null>(null);
  const [desconto, setDesconto] = useState<number | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);
  const moeda = docs[0]!.moeda;
  const saldo = docs.reduce((a, d) => a + d.saldo_centavos, 0);
  const n = parcelas ?? 0;
  const { total, parcela } = previaRenegociacao(saldo, juros ?? 0, multa ?? 0, desconto ?? 0, n);
  const valido = n >= 1 && n <= 36 && !!intervalo && intervalo >= 1 && /^\d{4}-\d{2}-\d{2}$/.test(primeiro) && total > 0 && !!motivo.trim();

  const renegociar = useMutation({
    mutationFn: () =>
      post<{ parcela_ids: string[] }>(
        '/contas/documentos/renegociar',
        {
          documento_ids: docs.map((d) => d.id),
          parcelas: n,
          primeiro_vencimento: primeiro,
          intervalo_dias: intervalo,
          juros_centavos: juros ?? 0,
          multa_centavos: multa ?? 0,
          desconto_centavos: desconto ?? 0,
          motivo: motivo.trim(),
        },
        { 'Idempotency-Key': chave },
      ),
    onSuccess: async (r) => {
      await invalidarContas(qc);
      toast.sucesso(t('w14.renegociado', { count: r.parcela_ids.length }));
      aoFeito(r.parcela_ids);
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('w14.renegTitulo', { count: docs.length })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!valido} carregando={renegociar.isPending} onClick={() => (setErro(null), renegociar.mutate())}>
            {t('w14.renegociar')}
          </Botao>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-renegociar>
        <p className="text-sm text-tinta-2 sm:col-span-3">{t('w14.renegTexto')}</p>
        <div className="flex min-w-0 flex-col rounded-lg border border-borda bg-sup-2 px-3 py-2 sm:col-span-3">
          <span className="kicker">{t('w14.saldoRenegociar')}</span>
          <TextoCortado className="numero text-lg font-semibold text-tinta-1">{dinheiro(saldo, moeda)}</TextoCortado>
          <TextoCortado className="numero text-xs text-tinta-3">{docs.map((d) => d.numero).join(', ')}</TextoCortado>
        </div>
        <CampoControlado def={{ nome: 'reneg_parcelas', rotulo: t('w14.parcelas'), tipo: 'numero', obrigatorio: true, min: 1, max: 36 }} valor={parcelas} aoMudar={(v) => setParcelas(v === null || v === '' ? null : Math.trunc(Number(v)))} />
        <CampoControlado def={{ nome: 'reneg_primeiro', rotulo: t('w14.primeiroVencimento'), tipo: 'data', obrigatorio: true }} valor={primeiro} aoMudar={(v) => setPrimeiro(String(v ?? ''))} />
        <CampoControlado def={{ nome: 'reneg_intervalo', rotulo: t('w14.intervalo'), tipo: 'numero', obrigatorio: true, min: 1, max: 365 }} valor={intervalo} aoMudar={(v) => setIntervalo(v === null || v === '' ? null : Math.trunc(Number(v)))} />
        <CampoControlado def={{ nome: 'reneg_juros', rotulo: t('w14.jurosNegociados'), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(moeda)) }} valor={juros} aoMudar={(v) => setJuros(v as number | null)} />
        <CampoControlado def={{ nome: 'reneg_multa', rotulo: t('w14.multaNegociada'), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(moeda)) }} valor={multa} aoMudar={(v) => setMulta(v as number | null)} />
        <CampoControlado def={{ nome: 'reneg_desconto', rotulo: t('w14.descontoNegociado'), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(moeda)) }} valor={desconto} aoMudar={(v) => setDesconto(v as number | null)} />
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2 sm:col-span-3">
          {t('w14.motivo')}
          <AreaSensivel rows={2} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
        {!motivo.trim() && <p className="text-xs text-tinta-3 sm:col-span-3">{t('w14.motivoObrigatorio')}</p>}
        <div className="flex min-w-0 flex-col rounded-lg border border-borda bg-sup-2 px-3 py-2 sm:col-span-3">
          <span className="kicker">{t('w14.totalNovo')}</span>
          <TextoCortado className={`numero text-xl font-semibold ${total > 0 ? 'text-tinta-1' : 'text-perigo'}`}>{dinheiro(Math.max(total, 0), moeda)}</TextoCortado>
          {n > 1 && total > 0 && <TextoCortado className="numero text-xs text-tinta-3">{t('w14.valorParcela', { valor: dinheiro(parcela, moeda) })}</TextoCortado>}
        </div>
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-3">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}
